import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { getProfile, getSkills, getEducation, getExperience } from "../api/profileAPI";
import { getPublishedPosts } from "../api/blogAPI";
import { fetchCertificates } from "../api/certificateAPI";
import { fetchPortofolios } from "../api/portofolioAPI";
import { trackVisit } from "../api/analyticsAPI";
import ExperienceMediaCards from "../components/ExperienceMediaCards";
import { sortExperience, calcDuration } from "../utils/sortExperience";

const BASE = process.env.REACT_APP_EXPRESS_API_BASE_URL || "http://localhost:3001";
const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BASE}${url}`;
};

// ─── Navbar ───────────────────────────────────────────────────────────────────
const NAV_ITEMS = ["Home", "About", "Skills", "Experience", "Education", "Portfolio", "Certificates", "Blog", "Contact"];

const Navbar = ({ activeSection }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    const el = document.getElementById(id.toLowerCase());
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-slate-950/90 backdrop-blur-lg border-b border-purple-900/30 shadow-lg shadow-purple-900/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <span className="font-black text-lg tracking-tight text-white">
          AH<span className="text-purple-400">.</span>
        </span>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {NAV_ITEMS.map((item) => (
            <button
              key={item}
              onClick={() => scrollTo(item)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                activeSection === item.toLowerCase()
                  ? "text-purple-300 bg-purple-500/10"
                  : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`}
            >
              {item}
            </button>
          ))}
        </nav>

        {/* Mobile hamburger */}
        <button
          className="md:hidden text-zinc-400 hover:text-white p-2"
          onClick={() => setMenuOpen((v) => !v)}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {menuOpen
              ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />}
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-slate-950/95 backdrop-blur-lg border-b border-purple-900/30 px-4 pb-4">
          {NAV_ITEMS.map((item) => (
            <button
              key={item}
              onClick={() => scrollTo(item)}
              className="block w-full text-left px-4 py-3 text-sm text-zinc-300 hover:text-white hover:bg-purple-500/10 rounded-lg transition-colors"
            >
              {item}
            </button>
          ))}
        </div>
      )}
    </header>
  );
};

// ─── Hero Section ─────────────────────────────────────────────────────────────
const HeroSection = ({ profile }) => {
  const name = profile?.full_name || "Affan Haidar Atstsabit";
  const headline = profile?.headline || "NOC Engineer · Cybersecurity Enthusiast";
  const photo = resolveImg(profile?.photo_url);

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background gradients */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-purple-950/40 to-slate-950" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl" />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: "linear-gradient(#a855f7 1px, transparent 1px), linear-gradient(to right, #a855f7 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-24 flex flex-col md:flex-row items-center gap-12 md:gap-20">
        {/* Text */}
        <div className="flex-1 text-center md:text-left">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-semibold mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            Available for opportunities
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight mb-4">
            {name.split(" ").map((word, i) => (
              <span key={i} className={i === 1 ? "text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400" : ""}>
                {word}{" "}
              </span>
            ))}
          </h1>
          <p className="text-lg text-zinc-400 font-medium mb-8">{headline}</p>

          <div className="flex flex-wrap gap-3 justify-center md:justify-start">
            <button
              onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-bold rounded-xl transition-all duration-200 shadow-lg shadow-purple-900/30 text-sm"
            >
              Explore My Work
            </button>
            {profile?.cv_pdf_url && (
              <a
                href={resolveImg(profile.cv_pdf_url)}
                download
                className="px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/30 text-white font-bold rounded-xl transition-all duration-200 text-sm"
              >
                Download CV (PDF)
              </a>
            )}
            {profile?.cv_docx_url && (
              <a
                href={resolveImg(profile.cv_docx_url)}
                download
                className="px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/30 text-white font-bold rounded-xl transition-all duration-200 text-sm"
              >
                Download CV (DOCX)
              </a>
            )}
          </div>

          {/* Social icons */}
          <div className="flex gap-3 justify-center md:justify-start mt-8">
            {[
              { href: profile?.show_linkedin ? profile?.social_linkedin : null, label: "LinkedIn", d: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" },
              { href: profile?.show_github ? profile?.social_github : null, label: "GitHub", d: "M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" },
              { href: profile?.show_instagram ? profile?.social_instagram : null, label: "Instagram", d: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" },
            ].filter((s) => s.href).map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                title={s.label}
                className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:border-purple-500/40 hover:bg-purple-500/10 flex items-center justify-center text-zinc-400 hover:text-purple-300 transition-all duration-200"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d={s.d} />
                </svg>
              </a>
            ))}
          </div>
        </div>

        {/* Photo */}
        <div className="flex-shrink-0 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600/30 to-blue-600/30 rounded-3xl blur-2xl scale-110" />
          <div className="relative w-48 h-60 sm:w-56 sm:h-72 rounded-3xl overflow-hidden border border-purple-500/20 shadow-2xl shadow-purple-900/30">
            {photo ? (
              <img src={photo} alt={name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-purple-900/60 to-slate-900 flex items-center justify-center text-6xl font-black text-purple-300">
                {name.charAt(0)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-zinc-600 animate-bounce">
        <span className="text-[10px] font-semibold uppercase tracking-widest">Scroll</span>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </section>
  );
};

// ─── About Section ────────────────────────────────────────────────────────────
const AboutSection = ({ profile }) => {
  const bio = profile?.description || "I'm a cybersecurity-oriented engineer with a strong foundation in network operations. I currently serve as a Level 1 NOC Engineer in the banking sector, operating in a 24×7 rotational shift. My focus is shifting toward vulnerability assessment, penetration testing, and incident response.";
  const doingNow = profile?.doing_now || "Cybersecurity · NOC Engineering · Full-Stack Development";

  return (
    <section id="about" className="py-24 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-950/10 to-transparent pointer-events-none" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative">
        <SectionHeader label="About Me" accent="purple" />
        <div className="grid md:grid-cols-2 gap-10 items-start">
          <div>
            <p className="text-zinc-300 leading-relaxed text-base mb-6">{bio}</p>
            {doingNow && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-300 text-sm font-semibold">
                <span className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" />
                {doingNow}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: "Email", value: "affanhaidar232@gmail.com", icon: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" },
              { label: "Birthday", value: "October 07, 2000", icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" },
              { label: "Location", value: "South Tangerang", icon: "M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z" },
              { label: "Status", value: "Open to Work", icon: "M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" },
            ].map((item) => (
              <div key={item.label} className="p-4 rounded-xl bg-white/3 border border-white/8 hover:border-purple-500/20 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                  </svg>
                  <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500">{item.label}</span>
                </div>
                <p className="text-sm text-zinc-200 font-medium">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── What I Do Section ────────────────────────────────────────────────────────
const WHAT_I_DO = [
  {
    title: "Cybersecurity",
    accent: "purple",
    border: "border-purple-800/30",
    color: "from-purple-950/40 to-zinc-900/50",
    iconColor: "text-purple-400",
    icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
    points: ["Vulnerability assessment for web apps & APIs (Nmap, Burp Suite, DVWA)", "Blue Team: log/packet analysis with Kibana (ELK), Wazuh SIEM, Wireshark, Nessus", "CTF-style labs: reconnaissance, enumeration, basic exploitation", "Security-first mindset with risk-based prioritization and reproducible reporting"],
  },
  {
    title: "Network (NOC L1)",
    accent: "blue",
    border: "border-blue-800/30",
    color: "from-blue-950/40 to-zinc-900/50",
    iconColor: "text-blue-400",
    icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" /></svg>,
    points: ["Real-time monitoring: Grafana, OpManager, Dynatrace, Elastic", "Incident triage & escalation to L2/L3 in 24×7 rotational shift", "UiPath automation for NOC report generation", "Excel-based trend mapping and Jira ticketing"],
  },
  {
    title: "Full-Stack Development",
    accent: "blue",
    border: "border-cyan-800/30",
    color: "from-cyan-950/40 to-zinc-900/50",
    iconColor: "text-cyan-400",
    icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>,
    points: ["React.js front-end with responsive, accessible UIs", "Custom Express + MySQL backend with REST API", "Structured QA: functional, regression, performance testing", "Multer file uploads, JWT auth, analytics tracking"],
  },
];

const WhatIDoSection = ({ profile }) => {
  if (profile?.show_what_i_do === 0) return null;
  return (
    <section id="whatido" className="py-24 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-950/10 to-transparent pointer-events-none" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative">
        <SectionHeader label="What I Do" accent="purple" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {WHAT_I_DO.map((item) => (
            <div key={item.title} className={`rounded-2xl border ${item.border} bg-gradient-to-br ${item.color} p-6 flex flex-col gap-4`}>
              <div className="flex items-center gap-3">
                <div className={item.iconColor}>{item.icon}</div>
                <h3 className="font-bold text-sm text-white">{item.title}</h3>
              </div>
              <ul className="space-y-2">
                {item.points.map((pt, i) => (
                  <li key={i} className="flex gap-2 text-xs text-zinc-300 leading-relaxed">
                    <span className={`mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full ${item.accent === "purple" ? "bg-purple-500" : item.iconColor === "text-cyan-400" ? "bg-cyan-500" : "bg-blue-500"}`} />
                    {pt}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Skills Section ───────────────────────────────────────────────────────────
const SkillsSection = ({ skills }) => {
  const hardSkills = skills.filter((s) => s.category === "hard");
  const softSkills = skills.filter((s) => s.category === "soft");
  if (!skills.length) return null;

  return (
    <section id="skills" className="py-24 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-950/10 to-transparent pointer-events-none" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative">
        <SectionHeader label="Skills" accent="blue" />
        <div className="grid md:grid-cols-2 gap-8">
          {[
            { label: "Hard Skills", data: hardSkills, color: "blue" },
            { label: "Soft Skills", data: softSkills, color: "purple" },
          ].map(({ label, data, color }) =>
            data.length > 0 ? (
              <div key={label} className="p-6 rounded-2xl bg-white/3 border border-white/8">
                <h3 className={`text-xs font-black uppercase tracking-widest mb-6 ${color === "blue" ? "text-blue-400" : "text-purple-400"}`}>
                  {label}
                </h3>
                <div className="space-y-4">
                  {data.map((s) => (
                    <div key={s.id}>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-zinc-200 font-medium">{s.name}</span>
                        {s.level != null && <span className="text-xs text-zinc-500">{s.level}%</span>}
                      </div>
                      {s.level != null && (
                        <div className="h-1.5 rounded-full bg-zinc-800">
                          <div
                            className={`h-1.5 rounded-full transition-all duration-700 ${color === "blue" ? "bg-gradient-to-r from-blue-500 to-cyan-400" : "bg-gradient-to-r from-purple-500 to-pink-400"}`}
                            style={{ width: `${Math.min(s.level, 100)}%` }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : null
          )}
        </div>
      </div>
    </section>
  );
};

// ─── Experience Section ───────────────────────────────────────────────────────
const ExperienceSection = ({ experience }) => {
  if (!experience.length) return null;
  const sorted = sortExperience(experience);

  return (
    <section id="experience" className="py-24 relative">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <SectionHeader label="Experience" accent="purple" />
        <div className="relative pl-6 border-l border-purple-900/40 space-y-10">
          {sorted.map((exp, i) => {
            const points = Array.isArray(exp.points) ? exp.points : (typeof exp.points === "string" ? (() => { try { return JSON.parse(exp.points || "[]"); } catch { return []; } })() : []);
            return (
              <div key={exp.id ?? i} className="relative">
                <div className="absolute -left-[29px] top-1 w-4 h-4 rounded-full bg-purple-600 border-2 border-slate-950 shadow-[0_0_12px_rgba(168,85,247,0.4)]" />
                <div className="p-5 rounded-2xl bg-white/3 border border-white/8 hover:border-purple-500/20 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 mb-2">
                    <div>
                      <h3 className="text-base font-bold text-white">{exp.role}</h3>
                      <p className="text-purple-400 text-sm font-semibold">{exp.company}</p>
                      {exp.employment_type && (
                        <span className="text-[10px] text-zinc-500 font-semibold uppercase tracking-wider">{exp.employment_type}</span>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className="text-xs text-zinc-500 font-semibold px-3 py-1 rounded-lg bg-zinc-800 border border-zinc-700">
                        {exp.start_date} — {exp.is_current ? "Present" : exp.end_date}
                      </span>
                      {calcDuration(exp.start_date, exp.end_date, exp.is_current) && (
                        <span className="text-[10px] text-purple-400/70">{calcDuration(exp.start_date, exp.end_date, exp.is_current)}</span>
                      )}
                    </div>
                  </div>
                  {points.length > 0 && (
                    <ul className="mt-3 space-y-1.5">
                      {points.map((pt, j) => (
                        <li key={j} className="flex gap-2 text-sm text-zinc-300">
                          <span className="mt-2 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-purple-500" />
                          {pt}
                        </li>
                      ))}
                    </ul>
                  )}
                  <ExperienceMediaCards media={exp.media} cardStyle="dark" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ─── Education Section ────────────────────────────────────────────────────────
const EducationSection = ({ education }) => {
  if (!education.length) return null;

  return (
    <section id="education" className="py-24 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-950/10 to-transparent pointer-events-none" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative">
        <SectionHeader label="Education" accent="blue" />
        <div className="grid md:grid-cols-2 gap-6">
          {education.map((edu, i) => (
            <div
              key={edu.id ?? i}
              className="p-6 rounded-2xl bg-white/3 border border-white/8 hover:border-blue-500/20 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-blue-600/10 border border-blue-600/20 flex items-center justify-center text-blue-400">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-bold text-white leading-tight">{edu.institution}</h3>
                  {edu.degree && <p className="text-blue-400 text-sm font-semibold mt-0.5">{edu.degree}</p>}
                  {edu.field && <p className="text-zinc-400 text-sm">{edu.field}</p>}
                  <p className="text-xs text-zinc-500 mt-2 font-semibold">
                    {edu.start_year} — {edu.end_year || "Present"}
                  </p>
                  {edu.description && (
                    <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{edu.description}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Blog Section ─────────────────────────────────────────────────────────────
const BlogSection = ({ posts }) => {
  if (!posts.length) return null;
  return (
    <section id="blog" className="py-24 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-950/10 to-transparent pointer-events-none" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative">
        <div className="flex items-end justify-between mb-12">
          <SectionHeader label="Blog" accent="purple" />
          <Link
            to="/blog"
            className="text-sm text-purple-400 hover:text-purple-300 font-semibold transition-colors flex items-center gap-1 mb-1"
          >
            View all
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.slice(0, 6).map((post) => {
            const cover = post.cover_img
              ? post.cover_img.startsWith("http") ? post.cover_img : `${BASE}${post.cover_img}`
              : null;
            return (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group flex flex-col bg-white/3 border border-white/8 hover:border-purple-500/30 rounded-2xl overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-xl hover:shadow-purple-900/20"
              >
                {cover ? (
                  <div className="h-40 overflow-hidden bg-slate-900">
                    <img src={cover} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                ) : (
                  <div className="h-40 bg-gradient-to-br from-purple-900/30 to-slate-900 flex items-center justify-center">
                    <svg className="w-10 h-10 text-purple-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                    </svg>
                  </div>
                )}
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="text-white font-bold text-sm leading-snug group-hover:text-purple-300 transition-colors line-clamp-2 mb-2">
                    {post.title}
                  </h3>
                  {post.excerpt && (
                    <p className="text-zinc-400 text-xs leading-relaxed line-clamp-2 flex-1">{post.excerpt}</p>
                  )}
                  <div className="flex items-center gap-3 mt-4 pt-4 border-t border-white/5">
                    {post.published_at && (
                      <span className="text-[10px] text-zinc-500 font-mono">
                        {new Date(post.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </span>
                    )}
                    {post.view_count != null && (
                      <>
                        <span className="text-zinc-700">·</span>
                        <span className="text-[10px] text-zinc-500">{post.view_count} views</span>
                      </>
                    )}
                    <span className="ml-auto text-[10px] font-bold text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                      Read
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ─── Portfolio Section ────────────────────────────────────────────────────────
const PortfolioSection = ({ projects }) => {
  const [showAll, setShowAll] = useState(false);
  if (!projects.length) return null;
  const visible = showAll ? projects : projects.slice(0, 6);
  return (
    <section id="portfolio" className="py-24 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-950/10 to-transparent pointer-events-none" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative">
        <SectionHeader label="Portfolio" accent="purple" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {visible.map((proj, i) => (
            <a
              key={proj.id ?? i}
              href={proj.link || undefined}
              target={proj.link ? "_blank" : undefined}
              rel="noopener noreferrer"
              className={`group flex flex-col bg-white/3 border border-white/8 rounded-2xl overflow-hidden transition-all duration-200 ${proj.link ? "hover:border-purple-500/30 hover:-translate-y-1 hover:shadow-xl hover:shadow-purple-900/20 cursor-pointer" : "cursor-default"}`}
            >
              {proj.img ? (
                <div className="h-40 overflow-hidden bg-slate-900">
                  <img src={resolveImg(proj.img)} alt={proj.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
              ) : (
                <div className="h-40 bg-gradient-to-br from-purple-900/20 to-slate-900 flex items-center justify-center">
                  <svg className="w-10 h-10 text-purple-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
              <div className="p-4 flex flex-col flex-1">
                <p className="text-white font-bold text-sm leading-snug group-hover:text-purple-300 transition-colors line-clamp-2">{proj.name}</p>
                {proj.category && (
                  <span className="mt-3 self-start text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20">
                    {proj.category}
                  </span>
                )}
              </div>
            </a>
          ))}
        </div>
        {projects.length > 6 && (
          <div className="flex justify-center mt-8">
            <button
              onClick={() => setShowAll(!showAll)}
              className="px-6 py-2.5 rounded-full border border-white/10 text-sm text-zinc-300 hover:text-white hover:border-purple-500/40 transition-all"
            >
              {showAll ? "Show Less" : `Show All (${projects.length})`}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

// ─── Certificate Section ──────────────────────────────────────────────────────
const CertificateSection = ({ certificates }) => {
  const [showAll, setShowAll] = useState(false);
  if (!certificates.length) return null;
  const visible = showAll ? certificates : certificates.slice(0, 6);
  return (
    <section id="certificates" className="py-24 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-950/10 to-transparent pointer-events-none" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative">
        <SectionHeader label="Certificates" accent="purple" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {visible.map((cert, i) => (
            <a
              key={cert.id ?? i}
              href={cert.link || undefined}
              target={cert.link ? "_blank" : undefined}
              rel="noopener noreferrer"
              className={`group flex flex-col bg-white/3 border border-white/8 rounded-2xl overflow-hidden transition-all duration-200 ${cert.link ? "hover:border-cyan-500/30 hover:-translate-y-1 hover:shadow-xl hover:shadow-cyan-900/20 cursor-pointer" : "cursor-default"}`}
            >
              {cert.img ? (
                <div className="h-36 overflow-hidden bg-slate-900">
                  <img src={resolveImg(cert.img)} alt={cert.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
              ) : (
                <div className="h-36 bg-gradient-to-br from-cyan-900/20 to-slate-900 flex items-center justify-center">
                  <svg className="w-10 h-10 text-cyan-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
              )}
              <div className="p-4 flex flex-col flex-1">
                <p className="text-white font-bold text-sm leading-snug group-hover:text-cyan-300 transition-colors line-clamp-2">{cert.name}</p>
                {cert.organizer && <p className="text-zinc-400 text-xs mt-1">{cert.organizer}</p>}
                {cert.category && (
                  <span className="mt-3 self-start text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                    {cert.category}
                  </span>
                )}
              </div>
            </a>
          ))}
        </div>
        {certificates.length > 6 && (
          <div className="flex justify-center mt-8">
            <button
              onClick={() => setShowAll(!showAll)}
              className="px-6 py-2.5 rounded-full border border-white/10 text-sm text-zinc-300 hover:text-white hover:border-cyan-500/40 transition-all"
            >
              {showAll ? "Show Less" : `Show All (${certificates.length})`}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

// ─── Contact Section ──────────────────────────────────────────────────────────
const ContactSection = ({ profile }) => (
  <section id="contact" className="py-24 relative">
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-950/20 to-transparent pointer-events-none" />
    <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center relative">
      <SectionHeader label="Contact" accent="purple" center />
      <p className="text-zinc-400 text-base mb-8">
        Want to work together or just say hi? Reach out directly.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <a
          href={`https://wa.me/628997779254?text=${encodeURIComponent("Halo Affan, saya melihat portfolio kamu dan ingin terhubung.")}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-green-600 hover:bg-green-500 text-white font-bold rounded-2xl transition-all duration-200 shadow-lg shadow-green-900/30 text-base"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.126.553 4.122 1.523 5.854L.057 23.882a.5.5 0 00.611.641l6.193-1.624A11.944 11.944 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.907 0-3.693-.502-5.241-1.381l-.374-.217-3.876 1.017 1.035-3.78-.237-.389A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
          WhatsApp
        </a>
        <a
          href={`mailto:affanhaidar232@gmail.com?subject=${encodeURIComponent("Hi Affan — from your portfolio")}&body=${encodeURIComponent("Halo Affan,\n\nSaya melihat portfolio kamu dan ingin terhubung.\n\n")}`}
          className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-bold rounded-2xl transition-all duration-200 shadow-lg shadow-purple-900/30 text-base"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
          Email
        </a>
      </div>
    </div>
  </section>
);

// ─── Footer ───────────────────────────────────────────────────────────────────
const Footer = () => (
  <footer className="border-t border-white/5 py-8 text-center">
    <p className="text-zinc-600 text-sm">
      © {new Date().getFullYear()} Affan Haidar Atstsabit · Built with React
    </p>
  </footer>
);

// ─── Shared Helpers ───────────────────────────────────────────────────────────
const SectionHeader = ({ label, accent = "purple", center = false }) => (
  <div className={`flex flex-col ${center ? "items-center" : ""} mb-12`}>
    <div className={`flex items-center gap-3 ${center ? "justify-center" : ""} mb-2`}>
      <div className={`h-px w-8 ${accent === "purple" ? "bg-purple-500" : "bg-blue-500"}`} />
      <span className={`text-[10px] font-black uppercase tracking-[0.3em] ${accent === "purple" ? "text-purple-400" : "text-blue-400"}`}>
        {label}
      </span>
      <div className={`h-px w-8 ${accent === "purple" ? "bg-purple-500" : "bg-blue-500"}`} />
    </div>
    <h2 className="text-3xl font-black text-white tracking-tight">
      {label.split(" ").map((w, i) => (
        <span key={i}>
          {i === 0 ? w : <span className={accent === "purple" ? "text-purple-400" : "text-blue-400"}>{" "}{w}</span>}
        </span>
      ))}
    </h2>
  </div>
);

// ─── Intersection observer for active section ─────────────────────────────────
function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const observers = ids.map((id) => {
      const el = document.getElementById(id);
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActive(id); },
        { threshold: 0.4 }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach((o) => o?.disconnect());
  }, [ids]);
  return active;
}

// ─── Root component ───────────────────────────────────────────────────────────
const ModernTheme = () => {
  const [profile, setProfile] = useState(null);
  const [skills, setSkills] = useState([]);
  const [education, setEducation] = useState([]);
  const [experience, setExperience] = useState([]);
  const [posts, setPosts] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [projects, setProjects] = useState([]);

  const sectionIds = ["home", "about", "skills", "experience", "education", "portfolio", "certificates", "blog", "contact"];
  const activeSection = useActiveSection(sectionIds);

  useEffect(() => {
    trackVisit("/");
    Promise.all([getProfile(), getSkills(), getEducation(), getExperience(), getPublishedPosts(), fetchCertificates(), fetchPortofolios()])
      .then(([prof, sk, edu, exp, bl, certs, projs]) => {
        setProfile(prof.profile ?? prof);
        setSkills(Array.isArray(sk) ? sk : []);
        setEducation(Array.isArray(edu) ? edu : []);
        setExperience(Array.isArray(exp) ? exp : []);
        setPosts(Array.isArray(bl) ? bl : []);
        setCertificates(Array.isArray(certs) ? certs : []);
        setProjects(Array.isArray(projs) ? projs : []);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Navbar activeSection={activeSection} />
      <HeroSection profile={profile} />
      <AboutSection profile={profile} />
      <WhatIDoSection profile={profile} />
      <SkillsSection skills={skills} />
      <ExperienceSection experience={experience} />
      <EducationSection education={education} />
      <PortfolioSection projects={projects} />
      <CertificateSection certificates={certificates} />
      <BlogSection posts={posts} />
      <ContactSection profile={profile} />
      <Footer />
    </div>
  );
};

export default ModernTheme;
